// Pure maths for the Stats page (phase 16) — FY/month bucketing and goal
// progress, unit-testable without a D1 binding. The worker stats route does
// the row fetching; everything date-shaped here is YYYY-MM-DD strings from
// melbourneToday()/business dates, so the buckets stay Melbourne-local.

import { melbourneDate } from './dates';
import { quarterOf } from './finance';
import type { StatsComparisonWindow } from './types';

/** Match the elapsed month/day of the primary FY; today is a Melbourne date. */
export function statsComparisonWindow(primaryYear: number, comparisonYear: number, today: string): StatsComparisonWindow {
  const fullYear = (year: number) => ({ from: `${year - 1}-07-01`, to: `${year}-06-30` });
  const primary = fullYear(primaryYear);
  const comparison = fullYear(comparisonYear);
  const yearToDate = today >= primary.from && today <= primary.to;
  if (yearToDate) {
    primary.to = today;
    const [calendarYear, month, day] = today.split('-').map(Number);
    const comparisonCalendarYear = calendarYear + comparisonYear - primaryYear;
    const lastDay = new Date(Date.UTC(comparisonCalendarYear, month, 0)).getUTCDate();
    comparison.to = `${comparisonCalendarYear}-${String(month).padStart(2, '0')}-${String(Math.min(day, lastDay)).padStart(2, '0')}`;
  }
  return { primary, comparison, year_to_date: yearToDate };
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const GOAL_METRICS = ['revenue_ex', 'leads', 'proposals_delivered', 'jobs_won'] as const;
export type GoalMetric = (typeof GOAL_METRICS)[number];
export type GoalPeriod = 'month' | 'quarter' | 'year';

/** Derives the stored period type from the existing canonical key formats. */
export function goalPeriodOf(periodKey: string): GoalPeriod | null {
  if (/^\d{4}-(0[1-9]|1[0-2])$/.test(periodKey)) return 'month';
  if (/^FY\d{2} Q[1-4]$/.test(periodKey)) return 'quarter';
  if (/^FY\d{2}$/.test(periodKey)) return 'year';
  return null;
}

interface GoalInvoice {
  amount_cents: number;
  gst_cents: number;
  issue_date: string | null;
  paid_date: string | null;
}

interface GoalLead {
  status: string;
  enquiry_date: string | null;
  created_at: number;
  updated_at: number;
  won_at: number | null;
}

export interface GoalSend {
  sent_date: string;
}

export interface CurrentGoalActuals {
  month: Record<GoalMetric, number>;
  quarter: Record<GoalMetric, number>;
}

/** One source of truth for current-month/current-quarter goal actuals. The
    caller supplies Melbourne's current keys; timestamps are converted back
    to Melbourne business dates before bucketing. */
export function currentGoalActuals(
  currentMonth: string,
  currentQuarter: string,
  invoices: readonly GoalInvoice[],
  leads: readonly GoalLead[],
  sends: readonly GoalSend[],
): CurrentGoalActuals {
  const empty = (): Record<GoalMetric, number> => ({
    revenue_ex: 0,
    leads: 0,
    proposals_delivered: 0,
    jobs_won: 0,
  });
  const actuals: CurrentGoalActuals = { month: empty(), quarter: empty() };

  const add = (metric: GoalMetric, date: string, amount = 1) => {
    if (monthKeyOf(date) === currentMonth) actuals.month[metric] += amount;
    if (quarterOf(date)?.key === currentQuarter) actuals.quarter[metric] += amount;
  };

  for (const invoice of invoices) {
    const date = invoice.paid_date ?? invoice.issue_date;
    if (date) add('revenue_ex', date, invoice.amount_cents - invoice.gst_cents);
  }
  for (const lead of leads) {
    add('leads', lead.enquiry_date ?? melbourneDate(lead.created_at));
    if (lead.status === 'won') {
      // Historic wins predate won_at, so their last update is the best
      // available approximation until/unless they are backfilled.
      add('jobs_won', melbourneDate(lead.won_at ?? lead.updated_at));
    }
  }
  for (const send of sends) add('proposals_delivered', send.sent_date);

  return actuals;
}

function isoDay(iso: string): number | null {
  if (!DATE_RE.test(iso)) return null;
  const year = Number(iso.slice(0, 4));
  const month = Number(iso.slice(5, 7));
  const day = Number(iso.slice(8, 10));
  const value = Date.UTC(year, month - 1, day) / MS_PER_DAY;
  const check = new Date(value * MS_PER_DAY);
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null;
  return value;
}

function businessDaysInclusive(fromDay: number, toDay: number): number {
  let count = 0;
  for (let day = fromDay; day <= toDay; day += 1) {
    const weekday = new Date(day * MS_PER_DAY).getUTCDay();
    if (weekday >= 1 && weekday <= 5) count += 1;
  }
  return count;
}

/** Fraction of Mon–Fri business days elapsed in an inclusive period,
    clamped to 0–1. Today counts when it is a weekday; weekends hold at the
    preceding Friday's fraction. */
export function businessDaysElapsedFraction(periodStart: string, periodEnd: string, today: string): number {
  const startDay = isoDay(periodStart);
  const endDay = isoDay(periodEnd);
  const todayDay = isoDay(today);
  if (startDay === null || endDay === null || todayDay === null || startDay > endDay) return 0;
  const total = businessDaysInclusive(startDay, endDay);
  if (total === 0) return todayDay >= endDay ? 1 : 0;
  if (todayDay < startDay) return 0;
  if (todayDay >= endDay) return 1;
  return businessDaysInclusive(startDay, todayDay) / total;
}

/** '2026-07-15' → '2026-07'. Null for non-dates. */
export function monthKeyOf(iso: string | null | undefined): string | null {
  if (!iso || !DATE_RE.test(iso)) return null;
  return iso.slice(0, 7);
}

/** '2026-07-15' → 'FY27' (AU FY named for its end year). */
export function fyKeyOf(iso: string | null | undefined): string | null {
  if (!iso || !DATE_RE.test(iso)) return null;
  return quarterOf(iso)!.key.split(' ')[0];
}

/** The 12 month keys of an AU financial year: FY27 → 2026-07 … 2027-06. */
export function fyMonths(fyKey: string): string[] {
  const m = /^FY(\d{2})$/.exec(fyKey);
  if (!m) return [];
  const endYear = 2000 + Number(m[1]);
  const months: string[] = [];
  for (let i = 0; i < 12; i++) {
    const month = ((6 + i) % 12) + 1; // Jul … Jun
    const year = month >= 7 ? endYear - 1 : endYear;
    months.push(`${year}-${String(month).padStart(2, '0')}`);
  }
  return months;
}

/** Month keys spanning [from, to] inclusive, capped (chart sanity). */
export function monthKeysBetween(from: string, to: string, cap = 24): string[] {
  const a = monthKeyOf(from);
  const b = monthKeyOf(to);
  if (!a || !b || a > b) return [];
  const keys: string[] = [];
  let year = Number(a.slice(0, 4));
  let month = Number(a.slice(5, 7));
  while (keys.length < cap) {
    const key = `${year}-${String(month).padStart(2, '0')}`;
    keys.push(key);
    if (key === b) break;
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return keys;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-07' → 'Jul' (chart axis label, mono). */
export function monthLabel(key: string): string {
  return MONTHS[Number(key.slice(5, 7)) - 1] ?? key;
}

/** Goal progress 0–1, clamped; null when the target is 0/invalid. */
export function goalProgress(target: number, actual: number): number | null {
  if (!Number.isFinite(target) || target <= 0) return null;
  return Math.min(1, Math.max(0, actual / target));
}

/** Wins ÷ decided, where wins = won + locked_in (confirmed work not yet
    converted to a project) and decided = wins + lost + ghosted.
    Open/postponed leads don't count against conversion; null until at least
    one lead has been decided. */
export function conversionRate(counts: Partial<Record<string, number>>): number | null {
  const won = (counts.won ?? 0) + (counts.locked_in ?? 0);
  const decided = won + (counts.lost ?? 0) + (counts.ghosted ?? 0);
  if (decided === 0) return null;
  return won / decided;
}

/** One acquisition row of the Stats "Paid vs organic" table. */
export interface LeadAcquisitionBucket {
  enquiries: number;
  won: number;
  locked_in: number;
  /** won + locked_in + lost + ghosted. */
  decided: number;
  /** (won + locked_in) ÷ decided; null until something is decided. */
  conversion: number | null;
  /** Sum of value_cents (ex GST) of won leads. */
  revenue_cents: number;
  /** Count of won leads (revenue lands on Convert). */
  converted: number;
}

export interface LeadAcquisitionStats {
  paid: LeadAcquisitionBucket;
  organic: LeadAcquisitionBucket;
  /** Leads with no acquisition tag. Outbound leads are in neither row. */
  untagged: number;
}

/** Paid vs organic over leads the caller has already windowed. Void leads
    are excluded; `acquisition` must match exactly. */
export function leadAcquisitionStats(
  leads: readonly { status: string; acquisition: string | null; value_cents: number | null }[],
): LeadAcquisitionStats {
  const bucket = (key: 'paid' | 'organic'): LeadAcquisitionBucket => {
    const counts: Record<string, number> = {};
    let revenue = 0;
    for (const lead of leads) {
      if (lead.status === 'void' || lead.acquisition !== key) continue;
      counts[lead.status] = (counts[lead.status] ?? 0) + 1;
      if (lead.status === 'won') revenue += lead.value_cents ?? 0;
    }
    const won = counts.won ?? 0;
    const lockedIn = counts.locked_in ?? 0;
    return {
      enquiries: Object.values(counts).reduce((sum, n) => sum + n, 0),
      won,
      locked_in: lockedIn,
      decided: won + lockedIn + (counts.lost ?? 0) + (counts.ghosted ?? 0),
      conversion: conversionRate(counts),
      revenue_cents: revenue,
      converted: won,
    };
  };
  return {
    paid: bucket('paid'),
    organic: bucket('organic'),
    untagged: leads.filter((lead) => lead.status !== 'void' && lead.acquisition === null).length,
  };
}

// ── EOFY "wrapped" snapshot (Phase 2.6) ───────────────────────────────────
// A year-in-review reshape of figures the Stats route already aggregates.
// Every date-selection rule below (which date represents a project/invoice,
// the ex-GST split) is the SAME rule the main route uses elsewhere in this
// file, just re-bucketed by a chosen FY instead of the page's ad hoc
// [from, to] window — so the wrapped numbers always reconcile.

/** Whole days from one YYYY-MM-DD date to another (to − from). Same idiom
    as invoiceReminders.ts's day-count maths. */
export function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(toIso) - Date.parse(fromIso)) / MS_PER_DAY);
}

/** Mean of a set of day-counts, rounded; null when there's nothing to
    average — the wrapped page hides the figure rather than showing 0. */
export function avgDays(days: readonly number[]): number | null {
  if (days.length === 0) return null;
  return Math.round(days.reduce((sum, d) => sum + d, 0) / days.length);
}

/** Percent change from `prior` to `current`, rounded; null when there's no
    prior-period baseline (division by zero is meaningless, not "0%"). */
export function pctChange(current: number, prior: number): number | null {
  if (prior === 0) return null;
  return Math.round(((current - prior) / prior) * 100);
}

/** The FY immediately before the given one: FY27 → FY26. Null once it would
    go negative (no such thing as "FY-1"). */
export function priorFYKey(fyKey: string): string | null {
  const m = /^FY(\d{2})$/.exec(fyKey);
  if (!m) return null;
  const n = Number(m[1]) - 1;
  return n < 0 ? null : `FY${String(n).padStart(2, '0')}`;
}

/** Distinct FY keys found across a set of dates, most-recent first — the
    EOFY wrapped FY-switcher's option list. Always includes `mustInclude`
    (the currently-open FY) even if it has no dated rows yet. */
export function availableFYs(dates: readonly (string | null | undefined)[], mustInclude: string): string[] {
  const keys = new Set<string>([mustInclude]);
  for (const d of dates) {
    const fy = fyKeyOf(d);
    if (fy) keys.add(fy);
  }
  return [...keys].sort().reverse();
}

/** Which date represents a project for reporting purposes — the same
    fallback chain used everywhere a project needs one canonical date
    (Stats project-type/avg-value aggregation, EOFY wrapped). One function so
    every caller reconciles. */
export function projectActivityDate(p: {
  delivered_date: string | null;
  shoot_date: string | null;
  start_date: string | null;
  due_date: string | null;
}): string | null {
  return p.delivered_date ?? p.shoot_date ?? p.start_date ?? p.due_date;
}

/** Which date an invoice's revenue lands on — paid date once paid, issue
    date otherwise. Same rule everywhere revenue is bucketed by month/FY. */
export function invoiceRevenueDate(inv: { issue_date: string | null; paid_date: string | null }): string | null {
  return inv.paid_date ?? inv.issue_date;
}

export interface WrappedInvoiceRow {
  client_id: string;
  client_name: string;
  amount_cents: number;
  gst_cents: number;
  issue_date: string | null;
  paid_date: string | null;
}

export interface WrappedLeadRow {
  status: string;
  enquiry_date: string | null;
  closed_date: string | null;
  created_at: number;
}

export interface WrappedProjectRow {
  video_type: string | null;
  delivered_date: string | null;
  shoot_date: string | null;
  start_date: string | null;
  due_date: string | null;
}

export interface WrappedStats {
  fy: string;
  prior_fy: string | null;
  revenue_ex_cents: number;
  prior_revenue_ex_cents: number;
  revenue_change_pct: number | null;
  revenue_by_client: { client_id: string; client_name: string; revenue_ex_cents: number }[];
  months: { month: string; count: number }[];
  project_count_by_type: { type: string; count: number }[];
  avg_days_lead_to_won: number | null;
  avg_days_shoot_to_delivered: number | null;
}

/** Builds the EOFY wrapped figures for one FY out of the same raw rows the
    Stats route already fetches (paid invoices, leads, projects) — no
    parallel D1 query, just a second bucketing pass with the exact date/
    revenue rules above. "Avg project value" / "N projects delivered" are
    deliberately NOT recomputed here — the caller already has a per-FY
    avg-value map from the existing route logic; reuse that verbatim. */
export function buildWrappedStats(
  fy: string,
  invoices: readonly WrappedInvoiceRow[],
  leads: readonly WrappedLeadRow[],
  projects: readonly WrappedProjectRow[],
): WrappedStats {
  const months = fyMonths(fy);
  const monthSet = new Set(months);
  const prior = priorFYKey(fy);

  let revenueEx = 0;
  let priorRevenueEx = 0;
  const byClient = new Map<string, { name: string; sum: number }>();
  for (const inv of invoices) {
    const date = invoiceRevenueDate(inv);
    const key = monthKeyOf(date);
    if (!key) continue;
    const ex = inv.amount_cents - inv.gst_cents;
    if (monthSet.has(key)) {
      revenueEx += ex;
      const slot = byClient.get(inv.client_id) ?? { name: inv.client_name, sum: 0 };
      slot.sum += ex;
      byClient.set(inv.client_id, slot);
    }
    if (prior && fyKeyOf(date) === prior) priorRevenueEx += ex;
  }
  const revenueByClient = [...byClient.entries()]
    .map(([client_id, { name, sum }]) => ({ client_id, client_name: name, revenue_ex_cents: sum }))
    .sort((a, b) => b.revenue_ex_cents - a.revenue_ex_cents)
    .slice(0, 5);

  const monthCounts = new Map(months.map((m) => [m, 0]));
  const typeCounts: Record<string, number> = {};
  const shootToDelivered: number[] = [];
  for (const p of projects) {
    const date = projectActivityDate(p);
    const key = monthKeyOf(date);
    if (key && monthCounts.has(key)) {
      monthCounts.set(key, (monthCounts.get(key) ?? 0) + 1);
      const type = p.video_type ?? 'Unspecified';
      typeCounts[type] = (typeCounts[type] ?? 0) + 1;
    }
    if (p.shoot_date && p.delivered_date) {
      const deliveredKey = monthKeyOf(p.delivered_date);
      if (deliveredKey && monthSet.has(deliveredKey)) {
        shootToDelivered.push(daysBetween(p.shoot_date, p.delivered_date));
      }
    }
  }

  const leadToWon: number[] = [];
  for (const lead of leads) {
    if (lead.status !== 'won' || !lead.closed_date) continue;
    const closedKey = monthKeyOf(lead.closed_date);
    if (!closedKey || !monthSet.has(closedKey)) continue;
    const start = lead.enquiry_date ?? melbourneDate(lead.created_at);
    leadToWon.push(daysBetween(start, lead.closed_date));
  }

  return {
    fy,
    prior_fy: prior,
    revenue_ex_cents: revenueEx,
    prior_revenue_ex_cents: priorRevenueEx,
    revenue_change_pct: pctChange(revenueEx, priorRevenueEx),
    revenue_by_client: revenueByClient,
    months: months.map((m) => ({ month: m, count: monthCounts.get(m) ?? 0 })),
    project_count_by_type: Object.entries(typeCounts)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count),
    avg_days_lead_to_won: avgDays(leadToWon),
    avg_days_shoot_to_delivered: avgDays(shootToDelivered),
  };
}
